/**
 * Central customer-facing content for the AI Shopping Agent.
 *
 * Every visible label, template, prompt, status, form message, URL, and agent
 * name is defined here and resolved by React.
 */
export const agentContentSettings = {
  defaultAgentName: 'The Cask Oracle',
  pageTitle: '{AgentName} | The Whisky Exchange',
  metaDescription: 'The definitive guide to the world’s finest whiskies and exceptional spirits.',

  hostPanel: {
    homepageAriaLabel: 'The Whisky Exchange homepage',
    brandLabel: 'THE WHISKY EXCHANGE',
    heading: 'Ask {AgentName}',
    subtitle: 'The definitive guide to the world’s finest whiskies and exceptional spirits.',
    description: 'From celebrated classics to hidden treasures, discover exceptional bottles for every taste, occasion and budget.',
    buttonText: 'Chat with {AgentName}',
    imageLabel: 'A WORLD OF FINE SPIRITS',
  },

  chatWindow: {
    windowTitle: '{AgentName}',
    welcomeHeading: 'Ask {AgentName}',
    welcomeText: 'Ask a question below or choose a suggestion, and let {AgentName} draw on a world of expertise.',
    inputPlaceholder: 'Ask a question to begin your conversation…',
    inputAriaLabel: 'Ask a question to begin your conversation…',
    submitButtonText: 'Send message',
    conversationAriaLabel: 'Conversation with {AgentName}',
    assistantResponseAriaLabel: 'Advisor response',
    suggestedQuestionsAriaLabel: 'Suggested questions',
    suggestedFollowUpQuestionsAriaLabel: 'Suggested follow-up questions',
    conversationMenuLabel: 'Conversation menu',
    newConversationMenuItem: 'Start new conversation',
    emailHistoryMenuItem: 'Email chat history',
    onlineStatusLabel: 'Online',
    expandControlLabel: 'Expand',
    restoreControlLabel: 'Restore',
    minimiseControlLabel: 'Minimise',
  },

  supportingActions: {
    ariaLabel: 'AI Shopping Agent beta options',
    suggestNameButtonText: 'SUGGEST A NAME',
    submitFeedbackButtonText: 'SUBMIT FEEDBACK',
  },

  disclaimer: {
    text: 'Responses are generated by AI and may not always be accurate.',
    linkText: 'Read TWE’s AI disclaimer.',
    linkUrl: 'https://www.thewhiskyexchange.com/customerservice/legal-terms#ai-disclaimer',
  },

  suggestedQuestions: [
    {label: 'Explore whisky by flavour', prompt: 'Recommend nine whiskies, grouped into three distinct flavour styles, with three bottles in each group. Include a mixture of regions and price points, and briefly explain why each bottle belongs in that group.'},
    {label: 'Find memorable gifts by budget', prompt: 'Recommend nine memorable drinks gifts, grouped into three different price ranges, with three products in each group. Include a mixture of whiskies and fine spirits, and briefly explain what makes each product a good gift.'},
    {label: 'Build my cocktail collection', prompt: 'Help me build a versatile cocktail collection using nine bottles. Group them by three different base-spirit categories, with three bottles in each category, and tell me which cocktails I could make with each selection.'},
    {label: 'Discover drinks around the world', prompt: 'Take me on a drinks journey around the world. Create nine country groups with three products from each country. Before showing the products in each group, include a short introduction explaining the country’s drinks tradition and why the three selections are distinctive. Use a varied mix of drinks and styles, with no repeated products.'},
    {label: 'Explore food pairing ideas', prompt: 'Create 12 different food-pairing groups covering wine, whisky, rum and other drinks categories. Start each group with a short summary of the food pairing, then recommend three suitable products and briefly explain why each works. Return exactly 12 groups with 36 unique products in total, without repeating any product.'},
    {label: 'Create my tasting journey', prompt: 'Create a nine-bottle tasting journey, grouped into three stages: light and fruity, rich and spicy, and smoky and bold. Recommend three bottles for each stage and present them in the best tasting order.'},
  ],

  response: {
    friendlyError: "Sorry, I couldn't complete that. Please try again.",
    retryButtonText: 'Try again',
    responseTimingPrefix: 'Answered in',
    thinkingPrefix: 'Thinking…',
    helpfulAnswerAriaLabel: 'Helpful answer',
    unhelpfulAnswerAriaLabel: 'Unhelpful answer',
    rateAnswerAriaLabel: 'Rate this answer',
    feedbackThanks: 'Thank you for your feedback.',
    preparationStatus: 'Preparing your recommendations.',
    curationMessages: [
      'Understanding what you’re looking for…',
      'Exploring our carefully selected range…',
      'Choosing the best matches for you…',
    ],
    completedAnnouncement: '{AgentName} has completed its response.',
  },

  products: {
    recommendedProductsAriaLabel: 'Recommended products',
    scrollLeftAriaLabel: 'Scroll {GroupTitle} left',
    scrollRightAriaLabel: 'Scroll {GroupTitle} right',
    viewProductAriaLabel: 'View {ProductName}',
    addToBasketAriaLabel: 'Add to basket: {ProductName}',
    addedToBasketAriaLabel: 'Added to basket: {ProductName}',
    addingToBasket: 'Adding…',
    addedToBasket: '✓ Added to basket',
    addToBasket: 'Add to basket',
    addToBasketFailed: 'Could not add. Please try again.',
    currentlyUnavailable: 'Currently unavailable',
    viewMoreProducts: 'View more products',
  },

  feedbackForms: {
    nameSuggestion: {
      title: 'Suggest a name for our AI Shopping Agent',
      intro: 'We would love your help naming our new AI Shopping Agent. Suggest a name and tell us why you think it would be a good fit.',
      success: 'Thank you for your suggestion. We appreciate you helping us find the right name.',
      suggestedNameLabel: 'Your suggested name',
      nameLabel: 'Your name',
      emailLabel: 'Your email address',
      reasonLabel: 'Why do you think this name is a good fit?',
      websiteLabel: 'Website',
      privacyNotice: 'By submitting this form, your name, email address, submission and technical details about your AI Shopping Agent conversation will be shared with The Whisky Exchange team so we can review it.',
      privacyPrefix: 'To find out how we use your data, please read our',
      privacyLinkText: 'Privacy Policy',
      privacyLinkUrl: 'https://www.thewhiskyexchange.com/customerservice/legal-privacy',
      submitButtonText: 'Submit name suggestion',
      submittingButtonText: 'Submitting…',
      cancelButtonText: 'Cancel',
      validation: {
        suggestedNameRequired: 'Please enter your suggested name.',
        nameRequired: 'Please enter your name.',
        emailRequired: 'Please enter your email address.',
        emailInvalid: 'Please enter a valid email address.',
        reasonRequired: 'Please tell us why you think this name is a good fit.',
        tooLong: 'Your response is too long. Please shorten it and try again.',
        submitFailed: 'We could not submit your information. Please check your connection and try again.',
      },
    },
    feedback: {
      title: 'Share your feedback',
      intro: 'Our new AI Shopping Agent is still in beta, and our team is working through some known issues. If you notice anything unusual or have an idea that could improve the experience, please let us know.',
      success: 'Thank you for your feedback. It has been shared with our team.',
      nameLabel: 'Your name',
      emailLabel: 'Your email address',
      feedbackLabel: 'Your feedback',
      websiteLabel: 'Website',
      privacyNotice: 'By submitting this form, your name, email address, submission and technical details about your AI Shopping Agent conversation will be shared with The Whisky Exchange team so we can review it.',
      privacyPrefix: 'To find out how we use your data, please read our',
      privacyLinkText: 'Privacy Policy',
      privacyLinkUrl: 'https://www.thewhiskyexchange.com/customerservice/legal-privacy',
      submitButtonText: 'Submit feedback',
      submittingButtonText: 'Submitting…',
      cancelButtonText: 'Cancel',
      validation: {
        nameRequired: 'Please enter your name.',
        emailRequired: 'Please enter your email address.',
        emailInvalid: 'Please enter a valid email address.',
        feedbackRequired: 'Please enter your feedback.',
        tooLong: 'Your response is too long. Please shorten it and try again.',
        submitFailed: 'We could not submit your information. Please check your connection and try again.',
      },
    },
  },

  developer: {
    loadingStatus: '{AgentName} loads when you first open it.',
    readyStatus: 'Instance ready. Select Chat with {AgentName} to begin.',
    activeStatus: 'Instance active. Minimise preserves the conversation.',
    waitingForEvents: 'Waiting for events. Open {AgentName} to begin.',
  },
} as const;

type ContentTree = string | readonly ContentTree[] | {[key: string]: ContentTree};

function resolveTokens<T extends ContentTree>(value: T, agentName: string): T {
  if (typeof value === 'string') return value.replaceAll('{AgentName}', agentName) as T;
  if (Array.isArray(value)) return value.map(item => resolveTokens(item, agentName)) as unknown as T;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveTokens(item, agentName)])) as T;
}

/** Resolves every customer-facing template from React's central content settings. */
export function resolveAgentContent() {
  const agentName = agentContentSettings.defaultAgentName;
  return {agentName, ...resolveTokens(agentContentSettings, agentName)};
}

/** Resolves contextual product/group placeholders from the same content source. */
export function resolveContentTemplate(template: string, tokens: Record<string, string>) {
  return Object.entries(tokens).reduce((value, [token, replacement]) => value.replaceAll(`{${token}}`, replacement), template);
}

export type ResolvedAgentContent = ReturnType<typeof resolveAgentContent>;
